import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import MainLayout from '../../../layouts/MainLayout';
import { Card, Tabs, Button, Table, Space, Tag, Popconfirm, message } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SettingOutlined, ToolOutlined } from '@ant-design/icons';
import { leavesSettingsApi, departmentsApi, designationsApi, rolesApi } from '../../../utils/api';
import LeaveTypeModal from '../../../components/hr/leaves/LeaveTypeModal';
import { TableSkeleton } from '../../../components/SkeletonLoaders';
import PermissionGate from '../../../components/PermissionGate';

const { TabPane } = Tabs;

const LeavesSettings = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'type');
  const [loading, setLoading] = useState(false);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [roles, setRoles] = useState([]);
  const [generalSettings, setGeneralSettings] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingLeaveType, setEditingLeaveType] = useState(null);
  const [departmentsMap, setDepartmentsMap] = useState({});
  const [designationsMap, setDesignationsMap] = useState({});

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await leavesSettingsApi.getSettings(activeTab);
      console.log('📥 Leaves Settings Response:', res);
      
      // Handle different response structures
      const responseData = res.data || res;
      const data = responseData?.data || responseData || {};
      
      console.log('📊 Parsed Data:', data);
      console.log('📋 Leave Types:', data.leave_types);
      
      if (activeTab === 'type') {
        const leaveTypesData = data.leave_types || [];
        const leaveTypesArray = Array.isArray(leaveTypesData) ? leaveTypesData : [];
        console.log('✅ Setting leave types:', leaveTypesArray);
        setLeaveTypes(leaveTypesArray);
      } else if (activeTab === 'general') {
        const leaveTypesData = data.leave_types || [];
        const leaveTypesArray = Array.isArray(leaveTypesData) ? leaveTypesData : [];
        setLeaveTypes(leaveTypesArray);
        setGeneralSettings(data.leave_settings || null);
      }
    } catch (error) {
      console.error('خطا در دریافت تنظیمات:', error);
      message.error('خطا در دریافت تنظیمات مرخصی');
    } finally {
      setLoading(false);
    }
  };

  const loadOptions = async () => {
    try {
      const [deptRes, desRes, rolesRes] = await Promise.all([
        departmentsApi.getAll(),
        designationsApi.getAll(),
        rolesApi.getAll(),
      ]);
      const deptList = deptRes.data || [];
      const desList = desRes.data || [];
      
      setDepartments(deptList);
      setDesignations(desList);
      
      // Create maps for quick lookup
      const deptMap = {};
      deptList.forEach((dept) => {
        deptMap[dept.id] = dept.team_name || dept.name;
      });
      setDepartmentsMap(deptMap);
      
      const desMap = {};
      desList.forEach((des) => {
        desMap[des.id] = des.name;
      });
      setDesignationsMap(desMap);
      
      // rolesApi.getAll() returns array directly or wrapped
      const rolesData = Array.isArray(rolesRes) ? rolesRes : (rolesRes?.data?.roles || rolesRes?.data || []);
      setRoles(rolesData);
    } catch (error) {
      console.error('خطا در دریافت گزینه‌ها:', error);
    }
  };

  useEffect(() => {
    loadData();
    if (activeTab === 'type') {
      loadOptions();
    }
  }, [activeTab]);

  useEffect(() => {
    if (modalVisible) {
      loadOptions();
    }
  }, [modalVisible]);

  const handleTabChange = (key) => {
    setActiveTab(key);
    setSearchParams({ tab: key });
  };

  const handleAdd = () => {
    setEditingLeaveType(null);
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingLeaveType(record);
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await leavesSettingsApi.deleteLeaveType(id);
      message.success('نوع مرخصی با موفقیت حذف شد');
      loadData();
    } catch (error) {
      console.error('خطا در حذف:', error);
      message.error('خطا در حذف نوع مرخصی');
    }
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setEditingLeaveType(null);
  };

  const handleModalSuccess = () => {
    handleModalClose();
    loadData();
  };

  const columns = [
    {
      title: 'نام نوع',
      dataIndex: 'type_name',
      key: 'type_name',
      render: (text, record) => (
        <div className="flex items-center gap-2">
          {record.color && (
            <div
              className="w-4 h-4 rounded-full"
              style={{ backgroundColor: record.color }}
            />
          )}
          <span>{text}</span>
        </div>
      ),
    },
    {
      title: 'نوع',
      dataIndex: 'leavetype',
      key: 'leavetype',
      render: (text) => (
        <Tag color={text === 'yearly' ? 'blue' : 'green'}>
          {text === 'yearly' ? 'سالانه' : 'ماهانه'}
        </Tag>
      ),
    },
    {
      title: 'تعداد مرخصی',
      dataIndex: 'no_of_leaves',
      key: 'no_of_leaves',
      render: (text) => text || '-',
    },
    {
      title: 'محدودیت ماهانه',
      dataIndex: 'monthly_limit',
      key: 'monthly_limit',
      render: (text) => text || '-',
    },
    {
      title: 'با حقوق',
      dataIndex: 'paid',
      key: 'paid',
      render: (text) => (
        <Tag color={text === 1 ? 'green' : 'default'}>
          {text === 1 ? 'بله' : 'خیر'}
        </Tag>
      ),
    },
    {
      title: 'انتقال',
      dataIndex: 'unused_leave',
      key: 'unused_leave',
      width: 140,
      render: (text, record) => {
        const map = {
          'carry forward': 'انتقال',
          lapse: 'سوختن',
          paid: 'پرداخت',
          encash: 'بازخرید',
        };
        const label = map[text] || text || '—';
        const max = record.max_carry_forward;
        return (
          <span className="custom-font text-xs">
            {label}
            {text === 'carry forward' && max != null ? ` (حداکثر ${max})` : ''}
          </span>
        );
      },
    },
    {
      title: 'فایل',
      dataIndex: 'requires_file',
      key: 'requires_file',
      width: 70,
      render: (v) => (
        <Tag color={v ? 'orange' : 'default'}>{v ? 'الزامی' : '—'}</Tag>
      ),
    },
    {
      title: 'تأیید',
      dataIndex: 'requires_approval',
      key: 'requires_approval',
      width: 90,
      render: (v) => (
        <Tag color={v === false ? 'green' : 'blue'}>
          {v === false ? 'خودکار' : 'مدیر'}
        </Tag>
      ),
    },
    {
      title: 'پیش‌آگهی / متوالی',
      key: 'advance_consec',
      width: 130,
      render: (_, record) => (
        <span className="custom-font text-xs text-slate-600">
          {record.min_advance_days != null ? `${record.min_advance_days} روز` : '۰'}
          {' / '}
          {record.max_consecutive_days != null ? record.max_consecutive_days : '∞'}
        </span>
      ),
    },
    {
      title: 'دپارتمان',
      dataIndex: 'department',
      key: 'department',
      render: (text, record) => {
        try {
          const deptIds = typeof record.department === 'string' 
            ? JSON.parse(record.department) 
            : (record.department || []);
          const deptNames = deptIds
            .map((id) => departmentsMap[Number(id)] || departmentsMap[id])
            .filter(Boolean)
            .sort(); // مرتب‌سازی نام‌ها
          
          if (deptNames.length === 0) {
            return <span style={{ color: '#999' }}>-</span>;
          }
          
          return (
            <Space size={[8, 8]} wrap>
              {deptNames.map((name, index) => (
                <Tag key={index} color="blue">
                  {name}
                </Tag>
              ))}
            </Space>
          );
        } catch (e) {
          return <span style={{ color: '#999' }}>-</span>;
        }
      },
    },
    {
      title: 'عنوان شغلی',
      dataIndex: 'designation',
      key: 'designation',
      render: (text, record) => {
        try {
          const desIds = typeof record.designation === 'string' 
            ? JSON.parse(record.designation) 
            : (record.designation || []);
          const desNames = desIds
            .map((id) => designationsMap[Number(id)] || designationsMap[id])
            .filter(Boolean)
            .sort(); // مرتب‌سازی نام‌ها
          
          if (desNames.length === 0) {
            return <span style={{ color: '#999' }}>-</span>;
          }
          
          return (
            <Space size={[8, 8]} wrap>
              {desNames.map((name, index) => (
                <Tag key={index} color="green">
                  {name}
                </Tag>
              ))}
            </Space>
          );
        } catch (e) {
          return <span style={{ color: '#999' }}>-</span>;
        }
      },
    },
    {
      title: 'عملیات',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <PermissionGate permission="manage_leave_setting">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              size="small"
            />
          </PermissionGate>
          <PermissionGate permission="manage_leave_setting">
            <Popconfirm
              title="آیا مطمئن هستید؟"
              onConfirm={() => handleDelete(record.id)}
              okText="بله"
              cancelText="خیر"
            >
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
                size="small"
              />
            </Popconfirm>
          </PermissionGate>
        </Space>
      ),
    },
  ];

  // ستون‌های نمایشی ساده برای تب تنظیمات عمومی
  const generalColumns = [
    {
      title: 'نام نوع',
      dataIndex: 'type_name',
      key: 'type_name',
    },
    {
      title: 'نوع',
      dataIndex: 'leavetype',
      key: 'leavetype',
      render: (text) => (text === 'yearly' ? 'سالانه' : 'ماهانه'),
    },
    {
      title: 'تعداد مرخصی',
      dataIndex: 'no_of_leaves',
      key: 'no_of_leaves',
    },
    {
      title: 'با حقوق',
      dataIndex: 'paid',
      key: 'paid',
      render: (text) => (text === 1 ? 'با حقوق' : 'بدون حقوق'),
    },
  ];

  return (
    <MainLayout>
      <div className="" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
          {/* Header */}
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 text-white flex items-center justify-center shadow-lg">
                <ToolOutlined className="text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0">
                  تنظیمات مرخصی
                </h1>
                <p className="text-gray-500 dark:text-gray-400 text-sm m-0">
                  مدیریت انواع مرخصی و تنظیمات عمومی
                </p>
              </div>
            </div>
            {activeTab === 'type' && (
              <PermissionGate permission="manage_leave_setting">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAdd}
                  className="bg-green-600 hover:bg-green-700 border-0"
                  style={{ fontFamily: 'CustomFont' }}
                >
                  افزودن نوع مرخصی
                </Button>
              </PermissionGate>
            )}
          </div>

          {/* Content */}
          <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
            {loading ? (
              <TableSkeleton rows={5} columns={4} />
            ) : (
              <Tabs
                activeKey={activeTab}
                onChange={handleTabChange}
                type="card"
                style={{ fontFamily: 'CustomFont' }}
              >
                <TabPane
                  tab={
                    <span style={{ fontFamily: 'CustomFont' }}>
                      <SettingOutlined className="mr-2" />
                      انواع مرخصی
                    </span>
                  }
                  key="type"
                >
                  <Table
                    columns={columns}
                    dataSource={leaveTypes}
                    rowKey="id"
                    pagination={{
                      pageSize: 10,
                      showSizeChanger: true,
                      showTotal: (total) => `مجموع ${total} نوع مرخصی`,
                    }}
                    style={{ fontFamily: 'CustomFont' }}
                  />
                </TabPane>

                <TabPane
                  tab={
                    <span style={{ fontFamily: 'CustomFont' }}>
                      <SettingOutlined className="mr-2" />
                      تنظیمات عمومی
                    </span>
                  }
                  key="general"
                >
                  <div className="space-y-6">
                    <Card className="bg-white dark:bg-gray-800">
                      <div className="space-y-3">
                        <h3 className="text-lg font-semibold" style={{ fontFamily: 'CustomFont' }}>
                          تنظیمات مجوز مدیر
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400" style={{ fontFamily: 'CustomFont' }}>
                          {generalSettings?.manager_permission === 'pre-approve'
                            ? 'تایید پیش از ثبت'
                            : generalSettings?.manager_permission || 'تعریف نشده'}
                        </p>
                      </div>
                    </Card>

                    <Card className="bg-white dark:bg-gray-800">
                      <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: 'CustomFont' }}>
                        انواع مرخصی (نمایش فقط خواندنی)
                      </h3>
                      <Table
                        columns={generalColumns}
                        dataSource={leaveTypes}
                        rowKey="id"
                        pagination={false}
                        style={{ fontFamily: 'CustomFont' }}
                      />
                    </Card>
                  </div>
                </TabPane>
              </Tabs>
            )}
          </div>
        </Card>

        {/* Modal برای ایجاد/ویرایش نوع مرخصی */}
        <LeaveTypeModal
          visible={modalVisible}
          onCancel={handleModalClose}
          onSuccess={handleModalSuccess}
          editingLeaveType={editingLeaveType}
          departments={departments}
          designations={designations}
          roles={roles}
        />
      </div>
    </MainLayout>
  );
};

export default LeavesSettings;
